import React, { useState, useRef, useEffect } from 'react';
import { Expand, X, TrashIcon, Settings } from 'lucide-react';
import { createPortal } from 'react-dom';
import Tooltip from './TooltipSimple';
import { TOOLTIP_GAP } from './tooltipConsts';
import TrapezoidHandle from './TrapezoidHandle';
import { TimeframeDropdown } from './TimeframeDropdown';
import { SolarFlareBadge } from './StandardSolarFlareBadge';
import { RECENT_TRADES_COLUMNS } from '../../config/recentTradesColumns';
import type { TimeRange } from '../../types/dashboard';
import {
  DASHBOARD_INDEPENDENT_TIMEFRAME_WIDGET_IDS,
  getDashboardWidgetTimeframe,
  type WidgetTimeframeToken,
} from '../../lib/dashboardTimeRange';

const TIMEFRAME_WIDGET_IDS = DASHBOARD_INDEPENDENT_TIMEFRAME_WIDGET_IDS;

const WIDGET_TIMEFRAME_STORAGE_PREFIX = 'tt_widget_timeframe.';
const DEFAULT_WIDGET_TIMEFRAME = '7D' as const;

const widgetTimeframeValues: Record<string, WidgetTimeframeToken> = {
  totalFees: '7D',
  winningVsLosing: '7D',
  totalGainLoss: '7D',
  averageDailyGainLoss: '7D',
  totalCommissions: '7D',
  numberOfWinningTrades: '7D',
  numberOfLosingTrades: '7D',
};

function getWidgetTimeframeDisplayLabel(value: WidgetTimeframeToken): string {
  switch (value) {
    case '24H':
      return 'Last 24 Hours';
    case '7D':
      return 'Last 7 Days';
    case '30D':
      return 'Last 30 Days';
    case '12M':
      return 'Last 12 Months';
    case 'YTD':
      return 'YTD';
    default:
      return 'Last 7 Days';
  }
}

function getWidgetTimeframeStorageKey(id: string) {
  return `${WIDGET_TIMEFRAME_STORAGE_PREFIX}${id}`;
}

function readWidgetTimeframe(id: string): WidgetTimeframeToken {
  try {
    const key = getWidgetTimeframeStorageKey(id);
    const raw = localStorage.getItem(key) ?? sessionStorage.getItem(key);
    if (raw === '24H' || raw === '7D' || raw === '30D' || raw === '12M' || raw === 'YTD') {
      localStorage.setItem(key, raw);
      return raw;
    }
  } catch {}
  return widgetTimeframeValues[id] ?? DEFAULT_WIDGET_TIMEFRAME;
}

function writeWidgetTimeframe(id: string, value: WidgetTimeframeToken) {
  try {
    const key = getWidgetTimeframeStorageKey(id);
    localStorage.setItem(key, value);
    sessionStorage.setItem(key, value);
  } catch {}
}
import { useAssetClassVisibility } from '../../lib/assetClassVisibility';
import { useClickAway } from '../../hooks/useClickAway';

interface WidgetCardProps {
  id: string;
  domId?: string;
  dragAttributes?: Record<string, any>;
  dragListeners?: Record<string, any>;
  dragActivatorRef?: React.Ref<HTMLDivElement>;
  title: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  size?: 'full' | 'regular' | 'mini' | 'large';
  headerContent?: React.ReactNode;
  headerCenter?: React.ReactNode;
  onRemove?: () => void;
  onResize?: (id: string, size: 'full' | 'regular' | 'mini' | 'large') => void;
  isResizable?: boolean;
  // Fired when a settings toggle is changed. key identifies the column; value is checked state
  onToggle?: (key: string, value: boolean) => void;
  // For openTrades: current visible columns to keep toggles in sync
  visibleColumns?: Record<string, boolean>;
  /** Disable native HTML drag when using a custom (pointer-based) grid engine */
  draggableEnabled?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLElement>, id: string) => void;
  onDragOver?: (e: React.DragEvent<HTMLElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLElement>, id: string) => void;
  onDragEnd?: (e: React.DragEvent<HTMLElement>) => void;
  // Show native tooltips on top-right controls (settings/resize/delete/move)
  enableTooltips?: boolean;
  // Optional overrides for tooltip text
  tooltipTexts?: Partial<Record<'move' | 'settings' | 'resize' | 'remove', string>>;
  dashboardTimeRange?: TimeRange;
  dashboardTimeRangeRevision?: number;
  // Optional inline controls to nudge full-width widgets up/down without dragging
  showVerticalReorder?: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

const WidgetCardImpl: React.FC<WidgetCardProps> = ({
  id,
  domId,
  dragAttributes,
  dragListeners,
  dragActivatorRef,
  title,
  children,
  className = '',
  style,
  size = 'regular',
  headerContent,
  headerCenter,
  onRemove,
  onResize,
  isResizable = true,
  onToggle,
  visibleColumns,
  onDragStart = (_e: React.DragEvent<HTMLElement>, _id: string) => {},
  onDragOver = (_e: React.DragEvent<HTMLElement>) => {},
  onDrop = (_e: React.DragEvent<HTMLElement>, _id: string) => {},
  onDragEnd = (_e: React.DragEvent<HTMLElement>) => {},
  draggableEnabled = true
  ,
  enableTooltips = true,
  tooltipTexts,
  dashboardTimeRange,
  dashboardTimeRangeRevision = 0,
}) => {
  const [isResizeMenuOpen, setResizeMenuOpen] = useState(false);
  const resizeMenuRef = useRef<HTMLDivElement>(null);
  const resizeButtonRef = useRef<HTMLButtonElement>(null);
  const [resizeMenuPos, setResizeMenuPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [isTimeframeMenuOpen, setTimeframeMenuOpen] = useState(false);
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const timeframeDropdownIds = new Set<string>(TIMEFRAME_WIDGET_IDS);

  const [visibility] = useAssetClassVisibility();
  const isStocksOnly = visibility.STOCKS && !visibility.FUTURES && !visibility.FOREX && !visibility.CRYPTO;

  const supportedColumns = React.useMemo(() => {
    return RECENT_TRADES_COLUMNS.filter((col: any) => {
      if (col.key === 'notes') return false;
      if (col.assetClass === 'FUTURES' && !visibility.FUTURES) return false;
      if (col.assetClass === 'FOREX' && !visibility.FOREX) return false;
      if (col.assetClass === 'CRYPTO' && !visibility.CRYPTO) return false;
      return true;
    }).map((col: any) => {
      if (isStocksOnly && col.key === 'pnlPercentEquities') return { ...col, label: 'P&L (%)' };
      if (isStocksOnly && col.key === 'volume') return { ...col, label: 'Shares' };
      return col;
    });
  }, [visibility, isStocksOnly]);

  const LARGE_ENABLED_IDS = new Set([
    'performanceByTimeOfDay',
    'performanceByRelativeVolatility',
    'performanceByVolumeTraded',
    'performanceByEntryAtr',
    'performanceByInTradePriceRange',
    'performanceByDuration',
    'performanceByDay',
    'performanceByPrice',
    'performanceByOpeningGap',
    'performanceByVolume',
    'performanceByMonth',
    'performanceByAtr',
    'performanceByMovement',
  ]);

  const [tfMap, setTfMap] = useState<Record<string, WidgetTimeframeToken>>(() => {
    const next: Record<string, WidgetTimeframeToken> = {};
    TIMEFRAME_WIDGET_IDS.forEach((id) => {
      next[id] = readWidgetTimeframe(id);
    });
    return next;
  });

  useEffect(() => {
    if (!dashboardTimeRange || !TIMEFRAME_WIDGET_IDS.includes(id as (typeof TIMEFRAME_WIDGET_IDS)[number])) return;

    const token = getDashboardWidgetTimeframe(dashboardTimeRange);
    setTfMap((previous) => previous[id] === token ? previous : { ...previous, [id]: token });
    try {
      window.dispatchEvent(new CustomEvent('widget-timeframe:selected', { detail: { id, value: token } }));
    } catch {}
  }, [dashboardTimeRange, dashboardTimeRangeRevision, id]);
  const displayToToken: Record<string, '24H' | '7D' | '30D' | '12M' | 'YTD'> = {
    'Last 24 Hours': '24H',
    'Last 7 Days': '7D',
    'Last 30 Days': '30D',
    'Last 12 Months': '12M',
    YTD: 'YTD',
  };
  const [settingsPos, setSettingsPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  // Stage changes locally; apply only on Save
  const [pending, setPending] = useState<Record<string, boolean> | null>(null);
  // Keys shown in the disabled section (not toggleable in this modal)
  const disabledKeys = new Set(['commission', 'commissionAndFees', 'initialRisk', 'pnlR']);
  const defaultTooltipText: Record<string, string> = {
    move: 'Move',
    settings: 'Settings',
    resize: 'Resize',
    remove: 'Remove',
  };

  const wrapWithTooltip = (key: 'move' | 'settings' | 'resize' | 'remove', node: React.ReactNode) => {
    const text = tooltipTexts?.[key] ?? defaultTooltipText[key];
    if (!enableTooltips) return node;
    if (key === 'move') return <Tooltip text={text} gap={TOOLTIP_GAP + 18}>{node}</Tooltip>;
    return <Tooltip text={text}>{node}</Tooltip>;
  };

  useClickAway(resizeMenuRef, (e) => {
    if (resizeButtonRef.current && resizeButtonRef.current.contains(e.target as Node)) return;
    setResizeMenuOpen(false);
  });
  useClickAway(settingsMenuRef, (e) => {
    if (settingsButtonRef.current && settingsButtonRef.current.contains(e.target as Node)) return;
    setSettingsOpen(false);
    setPending(null);
  });

  // Position the settings dropdown under the gear button and keep it within viewport
  React.useEffect(() => {
    if (!isSettingsOpen) return;
    const updatePos = () => {
      const btn = settingsButtonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const width = Math.min(440, window.innerWidth - 16); // matches the responsive settings panel width
      const padding = 8; // small offset from edges
      // Open directly below the button and align the menu's left edge with the button
      let left = rect.left;
      left = Math.max(padding, Math.min(left, window.innerWidth - width - padding));
      // place the menu immediately below the button (small 4px gap)
      const top = rect.bottom + 4;
      setSettingsPos({ top: top, left: left });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [isSettingsOpen]);

  const isAnyMenuOpen = isSettingsOpen || isResizeMenuOpen || isTimeframeMenuOpen;

  const borderClass = 'border border-border';

  // Make Open Trades use a solid background like Week Summary; others keep the subtle gradient
  const bgClass = id === 'openTrades'
    ? 'bg-white dark:bg-zinc-900'
    : 'bg-gradient-to-b from-white to-zinc-50 dark:from-zinc-900 dark:to-zinc-950';

  // Badge state updated by widgets via `widget-badge:update` custom events
  const [badgeMap, setBadgeMap] = useState<Record<string, { status?: string; color?: string; description?: string; limit?: number; secondaryLabel?: string }>>({});

  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (!detail || !detail.id) return;
        setBadgeMap(prev => ({
          ...prev,
          [detail.id]: {
            status: detail.status,
            color: detail.color,
            description: detail.description,
            limit: detail.limit,
            secondaryLabel: detail.secondaryLabel,
          },
        }));
      } catch {}
    };
    window.addEventListener('widget-badge:update', handler as EventListener);
    return () => window.removeEventListener('widget-badge:update', handler as EventListener);
  }, []);

  const paddingClass = 'px-4 pt-[10px] pb-[10px]';

  // Allow certain widgets to render overflow (badges) outside the content area
  const contentOverflowClass = (
    id === 'avgWinLoss' || id === 'holdTimeAnalysis' || id === 'largestGainLoss'
  ) ? 'overflow-visible' : 'overflow-hidden';

  // No activation guard; allow dragging from anywhere on the card immediately.

  return (
    <div
      id={domId ?? id}
      data-card-size={size}
      className={`widget-card group relative ${isAnyMenuOpen ? 'z-[100000]' : ''} rounded-lg ${borderClass} ${bgClass} ${paddingClass} flex flex-col shadow-sm hover:shadow-[0_6px_18px_rgba(37,99,235,0.16),0_0_22px_rgba(37,99,235,0.10),0_0_0_1px_rgba(37,99,235,0.14)] transition-shadow duration-150 overflow-visible ${className}`}
      style={style}
      draggable={draggableEnabled}
      onDragStart={(e) => {
        if ((window as any).__dashboard_dnd_active) {
          e.preventDefault();
          return;
        }
        if (!draggableEnabled) return;
        onDragStart(e, id);
      }}
      onDragEnd={(e) => {
        if ((window as any).__dashboard_dnd_active) {
          e.preventDefault();
          return;
        }
        if (!draggableEnabled) return;
        onDragEnd(e);
      }}
      onDragOver={(e) => {
        if ((window as any).__dashboard_dnd_active) { e.preventDefault(); return; }
        if (!draggableEnabled) return;
        onDragOver(e);
      }}
      onDrop={(e) => {
        if ((window as any).__dashboard_dnd_active) { e.preventDefault(); return; }
        if (!draggableEnabled) return;
        onDrop(e, id);
      }}
    >
      {/* Move handle: top centered trapezoid drag handle (wrapped with tooltip) */}
      {wrapWithTooltip('move', (
        <div
          ref={dragActivatorRef}
          className="inline-block touch-none cursor-grab active:cursor-grabbing"
          {...dragAttributes}
          {...dragListeners}
        >
          <TrapezoidHandle />
        </div>
      ))}

      {/* Header */}
      <div className="relative flex min-w-0 flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {id === 'openTrades' ? (
            // Match the Week Summary header styling exactly
            <h2 className="mb-2 min-w-0 break-words text-sm font-semibold uppercase text-zinc-600 dark:text-zinc-400">{title}</h2>
          ) : (
            <h3 className="flex min-h-8 w-full min-w-0 items-center text-[12px] font-semibold uppercase leading-4 tracking-wide text-zinc-700 dark:text-zinc-300">
              <span className="min-w-0 break-words">{title}</span>
            </h3>
          )}
        </div>
        {/* Badge for TotalCommissions: placed in the card header so it can be positioned independently */}
        {id === 'totalCommissions' && (() => {
          const b = badgeMap[id];
          const status = b?.status ?? 'HEALTHY';
          const color = b?.color ?? 'green';
          const desc = b?.description ?? '';
          const colorMap: Record<string, { bg: string; text: string }> = {
            // Colored background; label is white in light mode and dark in dark mode
            green: { bg: 'bg-emerald-600', text: 'text-white dark:text-zinc-900' },
            blue: { bg: 'bg-sky-600', text: 'text-white dark:text-zinc-900' },
            amber: { bg: 'bg-amber-600500', text: 'text-white dark:text-zinc-900' },
            red: { bg: 'bg-red-500', text: 'text-white dark:text-zinc-900' },
            gray: { bg: 'bg-zinc-300', text: 'text-white dark:text-zinc-900' },
          };
          const classes = colorMap[color] ?? colorMap.green;
          return (
            <div className="pointer-events-none absolute inset-x-0 top-[38px] z-10 flex items-start justify-between">
              <div className="pointer-events-auto">
                <span
                  data-testid="total-commissions-health-badge"
                  className={`mt-[2px] inline-flex items-center rounded-md px-3 py-1 text-xs font-semibold ${classes.bg} ${classes.text}`}
                  title={desc}
                >
                  <span>{status}</span>
                </span>
              </div>
              {b?.secondaryLabel && (
                <span
                  data-testid="total-commissions-drag"
                  className="whitespace-nowrap text-xs font-medium leading-6 text-zinc-400 dark:text-zinc-500"
                >
                  {b.secondaryLabel}
                </span>
              )}
            </div>
          );
        })()}
        {/* New Record badge previously centered; moved beside remove button (see controls area) */}
        {headerCenter && (
          <div className="pointer-events-none absolute left-1/2 transform -translate-x-1/2">
            <div className="pointer-events-auto">{headerCenter}</div>
          </div>
        )}

        <div
          data-testid="widget-header-controls"
          className="-mr-4 flex min-w-0 flex-wrap items-center justify-end gap-1"
        >
          {/* header controls */}
          {/* header controls */}
          {headerContent}
          {/* Timeframe dropdowns for supported widgets */}
          {/* Settings menu (only for Open Trades) */}
          {id === 'openTrades' && (
            <div className="relative">
              <button
                ref={settingsButtonRef}
                onClick={() => {
                  setSettingsOpen(o => {
                    const next = !o;
                    if (next) {
                      // Initialize staged values from current visibleColumns
                      const staged: Record<string, boolean> = {};
                      supportedColumns.forEach((c: any) => {
                        staged[c.key] = (visibleColumns?.[c.key] ?? false);
                      });
                      setPending(staged);
                        // debug logging removed for performance
                    } else {
                      setPending(null);
                    }
                    return next;
                  });
                }}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md text-zinc-500 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:text-blue-600 dark:focus:text-blue-400"
                aria-label={`Configure ${title} widget`}
                >
                {wrapWithTooltip('settings', <Settings size={17} />)}
              </button>
              {isSettingsOpen && createPortal(
                <div
                  ref={settingsMenuRef}
                  className="w-[440px] max-w-[calc(100vw-16px)] bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md border border-border/80 rounded-xl shadow-xl ring-1 ring-black/5 z-[2147483647] fixed overflow-auto max-h-[80vh] no-scrollbar animate-dropdown"
                  style={{ top: settingsPos.top, left: settingsPos.left }}
                >
                  {/* Header */}
                  <div className="px-5 pt-4 pb-3 border-b border-border flex items-start justify-between">
                    <div>
                      <div className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">Dashboard Column Settings</div>
                    </div>
                    <button type="button" aria-label="Close settings" onClick={() => setSettingsOpen(false)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800">
                      <X size={16} />
                    </button>
                  </div>
                  {/* Body */}
                  <div className="px-5 pt-2 pb-4">
                    <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">Select the columns you wish to display on your Dashboard.</p>
                    {/* Enabled toggles */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                      {supportedColumns.filter((col: any) => !disabledKeys.has(col.key)).map((col: any) => {
                        const value = (pending?.[col.key] ?? (visibleColumns?.[col.key] ?? false));
                        const isChecked = !!value;
                        return (
                          <label
                            key={col.key}
                            className="flex items-center gap-2 text-[12px] select-none text-zinc-800 dark:text-zinc-200 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              className="sr-only peer"
                              onChange={e =>
                                setPending(prev => ({ ...(prev || {}), [col.key]: e.target.checked }))
                              }
                            />
                            <span
                              className={`relative w-7 h-3 rounded-full shrink-0 transition-colors duration-200 ease-in-out after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:h-2 after:w-2 after:rounded-full after:bg-white after:shadow-sm after:transition-transform after:duration-200 after:ease-in-out ${
                                isChecked
                                  ? 'bg-blue-600 peer-checked:bg-blue-600 peer-checked:after:translate-x-[16px]'
                                  : 'bg-zinc-300'
                              }`}
                            />
                            <span>{col.label}</span>
                          </label>
                        );
                      })}
                    </div>

                    {/* Locked/premium columns are intentionally hidden in this dashboard menu */}
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-3 bg-zinc-50/80 dark:bg-zinc-900/60 rounded-b-xl flex items-center justify-between border-t border-border">
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setSettingsOpen(false); setPending(null); }} className="px-3 h-8 rounded-lg text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 border border-transparent hover:border-zinc-200 dark:hover:border-zinc-700">Cancel</button>
                      <button
                        onClick={() => {
                          if (!pending) { setSettingsOpen(false); return; }
                          // Apply diffs only for enabled toggles
                          supportedColumns.filter((c: any) => !disabledKeys.has(c.key)).forEach((c: any) => {
                            const handleKey = c.key;
                            const currentVal = !!(visibleColumns?.[c.key]);
                            const nextVal = !!pending[c.key];
                            if (nextVal !== currentVal) {
                              onToggle?.(handleKey, nextVal);
                            }
                          });
                          setSettingsOpen(false);
                          setPending(null);
                        }}
                        className="px-3 h-8 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                      >
                        Save changes
                      </button>
                    </div>
                  </div>
                </div>,
                document.body
              )}
            </div>
          )}

          {/* Resize menu */}
          <div className="relative" ref={resizeMenuRef}>
            {(isResizable && onResize && id !== 'averageTradePnl' && id !== 'openTrades' && id !== 'longTermInvestments' && !['largestGainLoss','totalGainLoss','totalFees','profitFactor','maxConsecutiveWins','maxConsecutiveLosses','tradePlStandardDeviation','averageDailyGainLoss','totalCommissions','numberOfWinningTrades','numberOfLosingTrades','winningVsLosing','largestGain','cumulativeDrawdown','dailyVolume','avgWinLoss','averagePerShareGainLoss','largestLoss','holdTimeAnalysis'].includes(id)) ? wrapWithTooltip('resize', (
                    <button
                      type="button"
                      aria-label={`Resize ${title} widget`}
                      ref={resizeButtonRef}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const btn = resizeButtonRef.current;
                        const wrap = resizeMenuRef.current;
                        if (btn && wrap) {
                          const btnRect = btn.getBoundingClientRect();
                          const wrapRect = wrap.getBoundingClientRect();
                          const menuWidth = 120; // max/min-width for the menu
                          let left = Math.round(btnRect.left - wrapRect.left);
                          if (btnRect.right > window.innerWidth - menuWidth) {
                              left = Math.round(btnRect.right - wrapRect.left - menuWidth);
                          }
                          const top = Math.round(btnRect.top - wrapRect.top + btnRect.height);
                          setResizeMenuPos({ left, top });
                        }
                        setResizeMenuOpen(o => !o);
                      }}
                      className="relative left-2.5 inline-flex h-10 w-10 items-center justify-center rounded-md text-zinc-500 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:text-white"
                    >
                    <Expand size={16} />
                  </button>
                )) : null}
              {isResizeMenuOpen && (
                <div
                  className="absolute bg-white dark:bg-zinc-900 border border-border rounded-lg shadow-lg ring-1 ring-black/5 z-[2147483647] w-auto min-w-[120px] max-w-[220px]"
                  style={{ left: resizeMenuPos.left, top: resizeMenuPos.top }}
                >
                  <ul className="py-1">
                    <li>
                      <button
                        onClick={(e) => { e.stopPropagation(); onResize!(id, 'regular'); setResizeMenuOpen(false); }}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 whitespace-nowrap"
                      >
                        Regular
                      </button>
                    </li>
                    {!LARGE_ENABLED_IDS.has(id) && (
                      <li>
                        <button
                          onClick={(e) => { e.stopPropagation(); onResize!(id, 'mini'); setResizeMenuOpen(false); }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 whitespace-nowrap"
                        >
                          Mini
                        </button>
                      </li>
                    )}
                    {LARGE_ENABLED_IDS.has(id) && (
                      <li>
                        <button
                          onClick={(e) => { e.stopPropagation(); onResize!(id, 'large'); setResizeMenuOpen(false); }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 whitespace-nowrap"
                        >
                          Large
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
          </div>

          {/* Timeframe dropdowns for supported widgets */}
          {timeframeDropdownIds.has(id) && (
            <div
              data-testid="widget-timeframe-control"
              className="relative z-[2147483647] ml-2 -mr-[1.5px] self-end -translate-y-[8.5px]"
            >
              <TimeframeDropdown
                value={getWidgetTimeframeDisplayLabel(tfMap[id] ?? DEFAULT_WIDGET_TIMEFRAME)}
                onChange={(range) => {
                  const token = displayToToken[range] ?? '7D';
                  setTfMap(prev => ({ ...prev, [id]: token }));
                  writeWidgetTimeframe(id, token);
                  try {
                    const ev = new CustomEvent('widget-timeframe:selected', { detail: { id, value: token } });
                    window.dispatchEvent(ev);
                  } catch {}
                }}
                onOpenChange={setTimeframeMenuOpen}
                className="origin-bottom-right scale-90"
              />
            </div>
          )}
          {/* Divider between settings and remove (zinc-500) for clarity */}
          {(id === 'openTrades' && onRemove) && (
            <div className="h-4 w-px bg-border/70 mx-1 self-center" aria-hidden />
          )}
          {/* Solar Flare badge (for largestGain): placed beside remove button */}
          {id === 'largestGain' && (
            <SolarFlareBadge label="All-Time High" className="ml-2 mr-1 -translate-y-[1px]" />
          )}
          {/* Remove button */}
          {onRemove && (
            wrapWithTooltip('remove', (
              <button type="button" aria-label={`Remove ${title} widget`} onClick={onRemove} className="mr-[3px] inline-flex h-10 w-10 items-center justify-center rounded-md text-zinc-500 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 dark:hover:text-red-600 dark:focus:text-red-400">
                <TrashIcon size={17} />
              </button>
            ))
          )}
        </div>
      </div>
      {/* Hairline under title: keep above content so widgets with negative margins don't overlap it */}
      <div className="widget-hairline w-full h-px bg-zinc-100 dark:bg-zinc-800 mb-3 relative z-30 pointer-events-none flex-shrink-0" />

      {/* Content with preview support for openTrades */}
      <div
        data-testid={`${id}-widget-content`}
        className={`relative z-0 flex flex-grow flex-col ${contentOverflowClass} ${id === 'avgWinLoss' ? '-mt-[5px]' : ''}`}
      >
        {(() => {
          if (id === 'openTrades' && pending && React.isValidElement(children)) {
            // debug logging removed for performance
            // Build preview visibility map based on current visibleColumns + pending staged values
            const preview: Record<string, boolean> = { ...(visibleColumns || {}) } as Record<string, boolean>;
            supportedColumns.forEach((c: any) => {
              if (typeof pending[c.key] === 'boolean') {
                preview[c.key] = pending[c.key] as boolean;
              }
            });
            return React.cloneElement(children as React.ReactElement<any>, { visibleColumns: preview });
          }
          return children;
        })()}
      </div>

      {/* Navigation chevrons removed for openTrades as requested */}
    </div>
    );
  };

  export const WidgetCard = React.memo(WidgetCardImpl, (prev, next) => {
    if (prev.id !== next.id) return false;
    if (prev.domId !== next.domId) return false;
    if (prev.title !== next.title) return false;
    if (prev.className !== next.className) return false;
    if (prev.size !== next.size) return false;
    if (prev.draggableEnabled !== next.draggableEnabled) return false;
    if (prev.enableTooltips !== next.enableTooltips) return false;
    if (prev.showVerticalReorder !== next.showVerticalReorder) return false;
    if (prev.canMoveUp !== next.canMoveUp) return false;
    if (prev.canMoveDown !== next.canMoveDown) return false;
    if (prev.headerContent !== next.headerContent) return false;
    if (prev.headerCenter !== next.headerCenter) return false;
    if (prev.onRemove !== next.onRemove) return false;
    if (prev.onResize !== next.onResize) return false;
    if (prev.onToggle !== next.onToggle) return false;
    if (prev.visibleColumns !== next.visibleColumns) return false;
    if (prev.children !== next.children) return false;
    const s1 = prev.style || {};
    const s2 = next.style || {};
    const k1 = Object.keys(s1 as Record<string, any>);
    const k2 = Object.keys(s2 as Record<string, any>);
    if (k1.length !== k2.length) return false;
    for (let i = 0; i < k1.length; i++) {
      const key = k1[i];
      if ((s1 as any)[key] !== (s2 as any)[key]) return false;
    }
    return true;
  });
